import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Divider, Form, FormInstance, Input, Modal, notification, Row, Select, Typography } from "antd";

import { IBancoSaldoDeContas, IContaSaldoDeContas, IFonteSaldoDeContas, ISolicitacaoAtivacao } from "../../../../../interfaces";
import { IOrgaoVinculado } from "../../../../../../Orgaos/interfaces";
import { IContaACadastrar } from "../../../../../../ContasACadastrar/interfaces";
import { ICreateConta, ICreateFonte, IGetFontes, IGetOrgaosVinculados, IUpdateConta } from "../../interfaces";

import { removeAcento } from "../../../../../../../../utils/RemoveAcentos";
import SolicitacoesDeAtivacaoService from "../../../../../../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService";
import ContasACadastrarService from "../../../../../../../../services/http/services/SaldoDeContas/ContasACadastrarService";
import OrgaoService from "../../../../../../../../services/http/services/SaldoDeContas/OrgaoService";
import { EmitirErro } from "../../../../../../../../services/EmitirErro";
import FonteForm from "../FonteForm";

interface IProps {
    visible: boolean;
    conta: IContaSaldoDeContas | null;
    contaACadastrar?: IContaACadastrar | null;
    onClose: () => void;
    solicitacao?: ISolicitacaoAtivacao;
    onContaCreated: (conta: IContaSaldoDeContas, contaACadastrarId?: number) => void;
    onContaUpdated?: (conta: IContaSaldoDeContas) => void;
    onCreateFonte?: (data: ICreateFonte) => Promise<IFonteSaldoDeContas | null>;
    onUpdateConta?: (data: IUpdateConta) => Promise<IContaSaldoDeContas | null>;
    onCreateConta?: (data: ICreateConta) => Promise<IContaSaldoDeContas | null>;
    onGetFontes?: (data: IGetFontes) => Promise<IFonteSaldoDeContas[] | null>;
    onGetOrgaosVinculados?: (data: IGetOrgaosVinculados) => Promise<IOrgaoVinculado[] | null>;
}

export default function ContaForm({
    visible,
    conta,
    onClose,
    solicitacao,
    onContaCreated,
    onContaUpdated,
    contaACadastrar,
    onCreateFonte,
    onUpdateConta,
    onCreateConta,
    onGetFontes,
    onGetOrgaosVinculados,
}: IProps) {
    const [isFonteFormVisible, setIsFonteFormVisible] = useState(false);
    const [isLoadingContaModalData, setIsLoadingContaModalData] = useState<boolean>(false);
    const [orgaosVinculados, setOrgaosVinculados] = useState<IOrgaoVinculado[] | null>(null);
    const [bancos, setBancos] = useState<IBancoSaldoDeContas[] | null>(null);
    const [fontes, setFontes] = useState<IFonteSaldoDeContas[] | null>(null);
    const [isFonteSelectOpen, setIsFonteSelectOpen] = useState(false);
    const [isSubmitingConta, setIsSubmitingConta] = useState(false);

    const formContaRef = useRef<FormInstance>(null);
    const loadedContaModalDataRef = useRef<boolean>(false);

    const disabledFieldsByContaACadastrar = !!contaACadastrar;

    const handleCloseFonteForm = useCallback(() => {
        setIsFonteFormVisible(false);
    }, []);

    const handleClickAdicionarFonte = useCallback(() => {
        setIsFonteFormVisible(true);
        setIsFonteSelectOpen(false);
    }, []);

    const handleFonteCreated = useCallback((fonte: IFonteSaldoDeContas) => {
        setFontes((prevState) => {
            if (!prevState) {
                return null;
            }

            const data = [...prevState];

            data.push(fonte);

            return data;
        });

        const fontesValue = formContaRef.current?.getFieldValue('fontes') || [];

        const novasFontes = [...fontesValue, fonte.id];

        formContaRef.current?.setFieldValue('fontes', novasFontes);
        handleCloseFonteForm();
    }, []);

    const handleClose = useCallback(() => {
        setIsFonteFormVisible(false);
        formContaRef.current?.setFieldsValue({
            contaId: null,
            orgaoVinculadoId: null,
            descricao: '',
            numero: '',
            agencia: '',
            bancoId: null,
            fontes: [],
        });
        onClose();
    }, []);

    const handleSubmitConta = useCallback(() => {
        try {
            formContaRef.current?.validateFields()
                .then(async (values: any) => {
                    try {
                        if (!values.fontes || values.fontes.length === 0) {
                            notification.error({
                                message: 'Erro',
                                description: 'Informe pelo menos 1 fonte de recurso para a conta.',
                            });
                            return;
                        }

                        setIsSubmitingConta(true);

                        if (contaACadastrar) {
                            const createContaResponse = await ContasACadastrarService.createConta({
                                agencia: contaACadastrar.agencia,
                                numero: contaACadastrar.numero,
                                bancoId: contaACadastrar.bancoId,
                                orgaoVinculadoId: contaACadastrar.orgaoVinculadoId,
                                descricao: values.descricao,
                                fonteIds: values.fontes,
                            });

                            notification.success({
                                message: 'Sucesso',
                                description: 'Conta cadastrada com sucesso!',
                            });

                            setIsSubmitingConta(false);
                            onContaCreated(createContaResponse, contaACadastrar.id);

                            handleClose();

                            return;
                        }

                        if (!solicitacao) {
                            return;
                        }

                        if (conta) {
                            const updatedContaResponse = await onUpdateConta?.({
                                solicitacaoId: solicitacao.id,
                                contaId: values.contaId,
                                descricao: values.descricao,
                                agencia: values.agencia,
                                numero: values.numero,
                                bancoId: values.bancoId,
                                orgaoVinculadoId: values.orgaoVinculadoId,
                                fonteIds: values.fontes,
                            });

                            if (!updatedContaResponse) {
                                return;
                            }

                            notification.success({
                                message: 'Sucesso',
                                description: 'Conta atualizada com sucesso!',
                            });

                            setIsSubmitingConta(false);
                            onContaUpdated?.(updatedContaResponse);

                            handleClose();

                            return;
                        }

                        const createContaResponse = await onCreateConta?.({
                            solicitacaoId: solicitacao.id,
                            descricao: values.descricao,
                            agencia: values.agencia,
                            numero: values.numero,
                            bancoId: values.bancoId,
                            orgaoVinculadoId: values.orgaoVinculadoId,
                            fonteIds: values.fontes,
                        });

                        if (!createContaResponse) {
                            return;
                        }

                        notification.success({
                            message: 'Sucesso',
                            description: 'Conta cadastrada com sucesso!',
                        });

                        setIsSubmitingConta(false);
                        onContaCreated(createContaResponse);

                        handleClose();
                    } catch (error) {
                        setIsSubmitingConta(false);
                        EmitirErro(error, true, 'submitConta_0213asldo2o3w0jasdmo0j2o');
                    }
                })
                .catch(() => {
                    notification.error({
                        message: 'Erro',
                        description:
                            'Preencha os campos obrigatorios!',
                    });
                });

        } catch (error) {
            EmitirErro(error, true, 'submitConta_0213asldo2o3w0jasdmo0j2o');
        }
    }, [conta, contaACadastrar]);

    const findDadosModalConta = useCallback(async () => {
        try {
            setIsLoadingContaModalData(true);

            const [
                orgaosVinculadosData,
                bancosData,
                fontesData,
            ] = await Promise.all([
                onGetOrgaosVinculados?.({ solicitacaoId: solicitacao?.id }) || null,
                solicitacao ? SolicitacoesDeAtivacaoService.getBancos({}) : null,
                contaACadastrar ? OrgaoService.getFontes({ orgaoId: contaACadastrar.orgaoVinculado.orgaoId }) : (onGetFontes?.({ solicitacaoId: solicitacao?.id }) || null),
            ]);

            setOrgaosVinculados(orgaosVinculadosData);
            setBancos(bancosData);
            setFontes(fontesData);
            setIsLoadingContaModalData(false);
            loadedContaModalDataRef.current = true;
        } catch (error) {
            setIsLoadingContaModalData(false);
            EmitirErro(error, true, 'findDadosModalConta_203oasdjasdno08jo12');
        }
    }, [solicitacao, contaACadastrar, onGetFontes]);

    useEffect(() => {
        if (visible && !loadedContaModalDataRef.current) {
            findDadosModalConta();
        }
    }, [visible, findDadosModalConta]);

    useEffect(() => {
        if (!conta) {
            return;
        }

        formContaRef.current?.setFieldsValue({
            contaId: conta.id,
            orgaoVinculadoId: conta.orgaoVinculadoId,
            descricao: conta.descricao,
            numero: conta.numero,
            agencia: conta.agencia,
            bancoId: conta.bancoId,
            fontes: conta.fontesConta.map((e) => e.fonteId),
        });
    }, [conta]);

    useEffect(() => {
        if (!contaACadastrar) {
            return;
        }

        formContaRef.current?.setFieldsValue({
            contaId: contaACadastrar.id,
            orgaoVinculadoId: contaACadastrar.orgaoVinculado.nome,
            descricao: '',
            numero: contaACadastrar.numero,
            agencia: contaACadastrar.agencia,
            bancoId: contaACadastrar.banco.nome,
            fontes: [],
        });
    }, [contaACadastrar]);

    return (
        <Modal
            title={conta ? "Editar conta" : "Cadastrar conta"}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            style={{ top: 20 }}
        >
            <Form
                ref={formContaRef}
                initialValues={{
                    contaId: conta?.id,
                    orgaoVinculadoId: conta?.orgaoVinculadoId,
                    descricao: conta?.descricao,
                    numero: conta?.numero,
                    agencia: conta?.agencia,
                    bancoId: conta?.bancoId,
                    fontes: conta?.fontesConta.map((e) => e.fonteId) || [],
                }}
                layout="vertical"
            >
                <Form.Item name="contaId" hidden>
                    <Input />
                </Form.Item>

                <Form.Item
                    name="orgaoVinculadoId"
                    label="Órgão vinculado"
                    rules={[
                        {
                            required: true,
                            message: 'Por favor selecione o órgão vinculado!',
                        },
                    ]}
                >
                    <Select
                        showSearch
                        disabled={disabledFieldsByContaACadastrar}
                        loading={isLoadingContaModalData}
                        placeholder="Selecione..."
                        filterOption={(input: any, option: any) => {
                            let textDigit = removeAcento(input)
                            let listCidade = removeAcento(option?.label);
                            return listCidade.indexOf(textDigit) >= 0;
                        }}
                        options={orgaosVinculados?.map((e) => ({ value: e.id, label: e.nome })) || []}
                    />
                </Form.Item>

                <Form.Item
                    name="bancoId"
                    label="Banco"
                    rules={[
                        {
                            required: true,
                            message: 'Por favor selecione o Banco!',
                        },
                    ]}
                >
                    <Select
                        showSearch
                        loading={isLoadingContaModalData}
                        disabled={disabledFieldsByContaACadastrar}
                        placeholder="Selecione..."
                        filterOption={(input: any, option: any) => {
                            let textDigit = removeAcento(input)
                            let listCidade = removeAcento(option?.label);
                            return listCidade.indexOf(textDigit) >= 0;
                        }}
                        options={bancos?.map((e) => ({ value: e.id, label: e.nome })) || []}
                    />
                </Form.Item>

                <Row gutter={16} >
                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="numero"
                            label="Número"
                            rules={[
                                {
                                    required: true,
                                    message: "Campo obrigatório!",
                                },
                            ]}
                        >
                            <Input placeholder="Informe o número da conta" disabled={disabledFieldsByContaACadastrar} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            name="agencia"
                            label="Agência"
                            rules={[
                                {
                                    required: true,
                                    message: "Campo obrigatório!",
                                },
                            ]}
                        >
                            <Input placeholder="Informe a agência" disabled={disabledFieldsByContaACadastrar} />
                        </Form.Item>
                    </Col>
                </Row>

                <Col>
                    <Form.Item
                        name="descricao"
                        label="Descrição"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Input placeholder="Informe a descrição" />
                    </Form.Item>
                </Col>

                <Form.Item
                    name="fontes"
                    label="Fontes"
                    rules={[
                        {
                            required: true,
                            message: 'Informe pelo menos 1 fonte de recurso para a conta.',
                            type: 'array',
                            min: 1,
                        },
                    ]}
                >
                    <Select
                        open={isFonteSelectOpen}
                        onDropdownVisibleChange={setIsFonteSelectOpen}
                        showSearch
                        allowClear
                        loading={isLoadingContaModalData}
                        mode="multiple"
                        placeholder="Selecione..."
                        optionLabelProp="title"
                        filterOption={(input: any, option: any) => {
                            let textDigit = removeAcento(input);
                            let searchableText = removeAcento(option?.searchValue || '');
                            return searchableText.indexOf(textDigit) >= 0;
                        }}
                        options={fontes?.map((e) => ({
                            value: e.id,
                            title: `${e.numero} - ${e.descricao}`,
                            searchValue: `${e.numero} - ${e.descricao} ${e.categoria?.nome || ''}`,
                            label: (
                                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, padding: '2px 0' }}>
                                    <span style={{ fontWeight: 500 }}>{e.numero} - {e.descricao}</span>
                                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                        {e.categoria?.nome}
                                    </Typography.Text>
                                </div>
                            ),
                        })) || []}
                        dropdownRender={!contaACadastrar ? menu => (
                            <div>

                                <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8 }}>
                                    <Button
                                        block
                                        onClick={handleClickAdicionarFonte}
                                        type="dashed"
                                    >
                                        Adicionar Fonte
                                    </Button>
                                </div>
                                <Divider style={{ margin: '4px 0' }} />
                                {menu}
                            </div>
                        ) : undefined}
                    />
                </Form.Item>

                {
                    isFonteFormVisible && solicitacao && (
                        <FonteForm
                            onCancel={handleCloseFonteForm}
                            solicitacaoId={solicitacao.id}
                            onFinish={handleFonteCreated}
                            onCreateFonte={onCreateFonte}
                        />
                    )
                }

                <Row style={{ marginTop: isFonteFormVisible ? 16 : 0 }} >
                    <Col span={24} style={{ textAlign: "end" }}>
                        <Button
                            style={{ marginRight: 10 }}
                            onClick={handleClose}
                        >
                            Cancelar
                        </Button>

                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={handleSubmitConta}
                            loading={isSubmitingConta}
                        >
                            Salvar conta
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
}
